import { useState, useEffect } from 'react';
import { api, ReliabilityBadge, HelpTip, PanelHowTo, useToast } from './_barrel.js';


        // The unified entity graph for a business: director-network, PSC
        // (beneficial-ownership) and directorship edges, each carrying its own
        // Admiralty provenance grade. Reads persisted edges; a rebuild button
        // re-projects them from the latest Companies House data.
        const EDGE_LABEL = {
            psc_of: 'Beneficial owners / controllers (PSC)',
            related_via_directors: 'Related companies (shared directors)',
            director_of: 'Directorships',
            associate_of: 'Known associates',
        };

        function EntityGraphPanel({ token, businessId, hasCompany }) {
            const [graph, setGraph] = useState(null);
            const [loading, setLoading] = useState(true);
            const [rebuilding, setRebuilding] = useState(false);
            const toast = useToast();

            const load = async () => {
                setLoading(true);
                try {
                    const g = await api(`/api/v1/businesses/${businessId}/graph`, {}, token);
                    setGraph(g);
                } catch (e) { /* leave empty */ } finally { setLoading(false); }
            };
            useEffect(() => { load(); }, [businessId, token]);

            // Ownership & Charges and Director Network both auto-load on first
            // open now, and each rebuilds this graph server-side as a side effect
            // once their own Companies House call lands. Re-fetch once, shortly
            // after mount, so this panel picks that up without the analyst having
            // to press Rebuild themselves — it only had "no relationships" before
            // because nothing had been loaded yet, not because there was nothing to find.
            useEffect(() => {
                if (!hasCompany) return;
                const t = setTimeout(() => { load(); }, 3500);
                return () => clearTimeout(t);
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [businessId, hasCompany]);

            const rebuild = async () => {
                setRebuilding(true);
                try {
                    const r = await api(`/api/v1/businesses/${businessId}/graph/rebuild`, { method: 'POST' }, token);
                    toast(`Graph rebuilt — ${r.edges_written} edge${r.edges_written === 1 ? '' : 's'}`);
                    await load();
                } catch (e) {
                    toast('Rebuild failed: ' + ((e.data && e.data.detail) || e.message));
                } finally { setRebuilding(false); }
            };

            const edges = (graph && graph.edges) || [];
            const groups = {};
            for (const e of edges) (groups[e.edge_type] = groups[e.edge_type] || []).push(e);

            return (
                <div className="panel mb-6">
                    <div className="panel-header flex items-center justify-between">
                        <span className="font-display text-xl gold">
                            Entity graph
                            <HelpTip text="One graph of this company's relationships — beneficial owners (PSC), related companies via shared directors, and directorships. Every edge carries its own source-reliability grade (Admiralty A–F) and information-credibility digit (1–6). Rebuilt from the latest Companies House data." />
                        </span>
                        <button className="btn-gold" style={{ padding: '6px 14px', fontSize: 13 }}
                                onClick={rebuild} disabled={rebuilding}>
                            {rebuilding ? 'Rebuilding…' : 'Rebuild graph'}
                        </button>
                    </div>
                    <PanelHowTo id="entity_graph" />
                    <div className="px-6 py-5">
                        {loading && <div className="text-sm text-muted">Loading…</div>}
                        {!loading && edges.length === 0 && (
                            <div className="text-sm text-muted" style={{ maxWidth: 640 }}>
                                {hasCompany
                                    ? 'No relationships resolved yet — this refreshes automatically a few seconds after the Ownership & Control and Director Network panels load, or press Rebuild to try again now.'
                                    : 'No Companies House record on file for this business, so there is nothing to map a relationship graph from.'}
                            </div>
                        )}
                        {!loading && Object.keys(groups).map((type) => (
                            <div key={type} className="mb-5">
                                <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">
                                    {EDGE_LABEL[type] || type} · {groups[type].length}
                                </div>
                                {groups[type].map((e) => {
                                    const other = e.edge_type === 'psc_of' ? e.src : e.dst;
                                    const prov = e.provenance || {};
                                    const c = { reliability: prov.reliability, reliability_label: prov.reliability_label,
                                                reliability_rationale: '', credibility: prov.credibility,
                                                credibility_label: prov.credibility_label, admiralty_code: prov.admiralty_code };
                                    const noc = (e.attributes && e.attributes.nature_of_control) || [];
                                    return (
                                        <div key={e.id} className="flex items-start gap-3 mb-2 pb-2"
                                             style={{ borderBottom: '1px solid var(--line)' }}>
                                            <ReliabilityBadge c={c} />
                                            <div style={{ minWidth: 0 }}>
                                                <div className="text-sm" style={{ color: 'var(--text)' }}>
                                                    {other.label}
                                                    {other.canonical || other.id ? (
                                                        <span className="ml-2 text-xs" style={{ color: 'var(--cyan)' }}>· in portfolio</span>
                                                    ) : null}
                                                    {e.attributes && e.attributes.is_dissolved ? (
                                                        <span className="ml-2 text-xs" style={{ color: 'var(--red)' }}>· dissolved</span>
                                                    ) : null}
                                                    {e.attributes && e.attributes.is_ceased ? (
                                                        <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>· ceased</span>
                                                    ) : null}
                                                </div>
                                                <div className="text-xs text-muted">
                                                    {prov.source} · {prov.admiralty_code}
                                                    {noc.length ? ` · ${noc.join(', ')}` : ''}
                                                    {e.attributes && e.attributes.shared_officers
                                                        ? ` · ${e.attributes.shared_officers} shared director${e.attributes.shared_officers === 1 ? '' : 's'}` : ''}
                                                    {e.attributes && e.attributes.role ? ` · ${e.attributes.role}` : ''}
                                                </div>
                                                {e.attributes && e.attributes.possible_registered_overseas_entity ? (
                                                    <div className="text-xs mt-1" style={{ color: 'var(--gold)' }}>
                                                        Possible UK Register of Overseas Entities match — not confirmed.{' '}
                                                        <a href={e.attributes.overseas_entity_check_url} target="_blank"
                                                           rel="noreferrer" style={{ color: 'var(--cyan)', textDecoration: 'underline' }}>
                                                            Check Companies House
                                                        </a>
                                                    </div>
                                                ) : null}
                                                {e.attributes && e.attributes.registered_overseas_entity_number ? (
                                                    <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                                        Resolved to Register of Overseas Entities record{' '}
                                                        {e.attributes.registered_overseas_entity_number} by automated{' '}
                                                        {e.attributes.roe_identity_match_corroborated_by_registration_number
                                                            ? 'name + registration-number' : 'name-only'} matching
                                                        {' '}&mdash; the ownership itself is a direct Companies House disclosure once this identity link is accepted.
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

export { EntityGraphPanel };
